import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  IconButton,
  Tooltip,
  Card,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ProductType } from "../../interfaces/DTO/mislaka_xml_models";
import { stockExposureBuckets, groupNumericFieldsBySection, numericFields, getFieldExplanation, supportedProductTypes, stockExposureRanges, stockExposureColors, foreignExposureRanges, foreignExposureColors, currencyExposureRanges, currencyExposureColors } from "../../utils/fundsFormatter";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import { formatProductType } from "../../utils/enumFormatter";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import {
  HandlersFilterList,
  SubSpecializationsFilterList,
  ExposureRangesFilterList,
} from "./filterLists";
import {
  ChartType,
  FundPopulation,
  DisplayPreferences,
  GemelDataFilteredView,
} from "./types";

// Re-export for backward compatibility
export { ChartType };
export type { FundPopulation, DisplayPreferences, GemelDataFilteredView };

interface GemelInvestigationPanelProps {
  filters: GemelDataFilteredView;
  onFiltersChange: (filters: GemelDataFilteredView) => void;
  hideProductTypeSelection?: boolean;
}

const GemelInvestigationPanel: React.FC<GemelInvestigationPanelProps> = ({
  filters,
  onFiltersChange,
  hideProductTypeSelection = false,
}) => {
  const theme = useTheme();

  // State לדיאלוג הסבר שדה
  const [explanationDialogOpen, setExplanationDialogOpen] = useState(false);
  const [selectedFieldExplanation, setSelectedFieldExplanation] = useState<{
    label: string;
    explanation: string;
  } | null>(null);

  const groupedFields = useMemo(() => groupNumericFieldsBySection(numericFields), []);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>({
    filters: true,
    yAxis: true,
    charts: true,
  });



  // רשימת גרפים זמינים
  const availableCharts = [
    { value: ChartType.YieldBubbles, label: "תשואות (בועות)" },
    { value: ChartType.FundYieldsChart, label: "גרף תשואות קרנות" },
  ];

  useEffect(() => {
    const defaultExpanded: Record<string, boolean> = {};
    Object.keys(groupedFields).forEach((section) => {
      defaultExpanded[section] = false;
    });
    setExpandedSections(defaultExpanded);
  }, [groupedFields]);

  const handleToggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleProductTypeChange = (productType: ProductType) => {
    onFiltersChange({ ...filters, productType });
  };

  const handleHandlerToggle = (handlerCode: string) => {
    const newHandlers: string[] = filters.handlers.includes(handlerCode)
      ? filters.handlers.filter((h: string) => h !== handlerCode)
      : [...filters.handlers, handlerCode];

    const newFilter: GemelDataFilteredView = {
      ...filters,
      handlers: newHandlers
    };
    onFiltersChange(newFilter);
  };

  const handleSubSpecializationToggle = (subSpec: string) => {
    const newSubSpecs: string[] = filters.subSpecializations.includes(subSpec)
      ? filters.subSpecializations.filter((s: string) => s !== subSpec)
      : [...filters.subSpecializations, subSpec];

    const newFilter: GemelDataFilteredView = {
      ...filters,
      subSpecializations: newSubSpecs
    };
    onFiltersChange(newFilter);
  };

  const handleStockExposureRangeToggle = (range: string) => {
    const newRanges: string[] = filters.stockExposureRanges.includes(range)
      ? filters.stockExposureRanges.filter((r: string) => r !== range)
      : [...filters.stockExposureRanges, range];

    const newFilter: GemelDataFilteredView = {
      ...filters,
      stockExposureRanges: newRanges
    };
    onFiltersChange(newFilter);
  };

  const handleForeignExposureRangeToggle = (range: string) => {
    const newRanges: string[] = filters.foreignExposureRanges.includes(range)
      ? filters.foreignExposureRanges.filter((r: string) => r !== range)
      : [...filters.foreignExposureRanges, range];

    const newFilter: GemelDataFilteredView = {
      ...filters,
      foreignExposureRanges: newRanges
    };
    onFiltersChange(newFilter);
  };

  const handleCurrencyExposureRangeToggle = (range: string) => {
    const newRanges: string[] = filters.currencyExposureRanges.includes(range)
      ? filters.currencyExposureRanges.filter((r: string) => r !== range)
      : [...filters.currencyExposureRanges, range];

    const newFilter: GemelDataFilteredView = {
      ...filters,
      currencyExposureRanges: newRanges
    };
    onFiltersChange(newFilter);
  };

  // PIN handlers
  const handleTogglePin = (filterName: string, value: string) => {
    const currentPinned: string[] = filters.displayPreferences.pinnedItems[filterName] || [];
    const newPinned: string[] = currentPinned.includes(value)
      ? currentPinned.filter((v: string) => v !== value)
      : [...currentPinned, value];

    const newPinnedItems = {
      ...filters.displayPreferences.pinnedItems,
      [filterName]: newPinned,
    };

    // persist pinnedItems to localStorage
    localStorage.setItem('gemelInvestigation_pinnedItems', JSON.stringify(newPinnedItems));

    const newDisplayPreferences: DisplayPreferences = {
      ...filters.displayPreferences,
      pinnedItems: newPinnedItems,
    };

    const newFilter: GemelDataFilteredView = {
      ...filters,
      displayPreferences: newDisplayPreferences
    };
    onFiltersChange(newFilter);
  };

  const handleBucketToggle = (bucketLabel: string) => {
    const newBuckets: string[] = filters.buckets.includes(bucketLabel)
      ? filters.buckets.filter((b: string) => b !== bucketLabel)
      : [...filters.buckets, bucketLabel];

    const newFilter: GemelDataFilteredView = {
      ...filters,
      buckets: newBuckets
    };
    onFiltersChange(newFilter);
  };

  const handleColorByToggle = (colorByValue: string) => {
    const newColorBy = filters.displayPreferences.colorBy === colorByValue ? 'default' : colorByValue;
    const newDisplayPreferences: DisplayPreferences = {
      ...filters.displayPreferences,
      colorBy: newColorBy as any,
    };
    const newFilter: GemelDataFilteredView = {
      ...filters,
      displayPreferences: newDisplayPreferences
    };
    onFiltersChange(newFilter);
  };

  const handleChartToggle = (chartType: ChartType) => {
    const newCharts: ChartType[] = filters.charts.includes(chartType)
      ? filters.charts.filter((c: ChartType) => c !== chartType)
      : [...filters.charts, chartType];

    const newFilter: GemelDataFilteredView = {
      ...filters,
      charts: newCharts
    };
    onFiltersChange(newFilter);
  };

  const handleYAxisFieldChange = (field: keyof FundDataBasic) => {
    const newYAxisField = filters.yAxisField === field ? undefined : field;

    const newFilter: GemelDataFilteredView = {
      ...filters,
      yAxisField: newYAxisField
    };
    onFiltersChange(newFilter);
  };

  const handleForGeneralPublicChange = (value: boolean | undefined) => {
    const newFilter: GemelDataFilteredView = {
      ...filters,
      forGeneralPublic: value
    };
    onFiltersChange(newFilter);
  };

  const handleOpenExplanation = (field: keyof FundDataBasic, label: string) => {
    const explanation = getFieldExplanation(field);
    if (explanation) {
      setSelectedFieldExplanation({ label, explanation });
      setExplanationDialogOpen(true);
    }
  };

  const handleCloseExplanation = () => {
    setExplanationDialogOpen(false);
    setSelectedFieldExplanation(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 1,
        maxHeight: "100%",
        overflowY: "auto",
        direction: "rtl",
      }}
    >

      {!hideProductTypeSelection && (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, textAlign: "right" }}>
            סוג מוצר
          </Typography>
          <List dense sx={{ bgcolor: theme.palette.background.paper, borderRadius: 1 }}>
            {supportedProductTypes.map((pt) => (
              <ListItem key={pt} disablePadding>
                <ListItemButton
                  selected={filters.productType === pt}
                  onClick={() => handleProductTypeChange(pt)}
                  sx={{
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.contrastText,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <ListItemText primary={formatProductType(pt)} sx={{ textAlign: "right" }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

    <Box>
        <Typography
          variant="subtitle1"
          onClick={() => setSectionOpen(prev => ({ ...prev, filters: !prev.filters }))}
          sx={{ mb: 1, fontWeight: 600, textAlign: "right", bgcolor: 'grey.800', color: 'white', px: 1.5, py: 0.5, borderRadius: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          סינון מסלולים
          <ExpandMoreIcon sx={{ color: 'white', transform: sectionOpen.filters ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
        </Typography>
      <Collapse in={sectionOpen.filters}>
      {/* פילטר אוכלוסיית יעד */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 0.5, textAlign: "right", color: "text.secondary" }}>
          אוכלוסיית יעד
        </Typography>
        <ToggleButtonGroup
          value={filters.forGeneralPublic === true ? "general" : "all"}
          exclusive
          onChange={(_e, newValue) => {
            if (newValue === "general") {
              handleForGeneralPublicChange(true);
            } else if (newValue === "all") {
              handleForGeneralPublicChange(undefined);
            }
          }}
          size="small"
          fullWidth
          sx={{ direction: "ltr" }}
        >
          <ToggleButton value="general" sx={{ flex: 1 }}>
            לציבור הרחב
          </ToggleButton>
          <ToggleButton value="all" sx={{ flex: 1 }}>
            הכל
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {/* יצרנים */}
      <HandlersFilterList
        selectedHandlers={filters.handlers}
        onToggle={handleHandlerToggle}
        pinnedHandlers={filters.displayPreferences.pinnedItems.handlers}
        onTogglePin={(value) => handleTogglePin('handlers', value)}
        colorByMode={filters.displayPreferences.colorBy}
        onColorByToggle={() => handleColorByToggle('managing_corporation')}
      />

      {/* התמחויות */}
      <SubSpecializationsFilterList
        productType={filters.productType}
        selectedSubSpecializations={filters.subSpecializations}
        onToggle={handleSubSpecializationToggle}
        pinnedSubSpecializations={filters.displayPreferences.pinnedItems.subSpecializations}
        onTogglePin={(value) => handleTogglePin('subSpecializations', value)}
        colorByMode={filters.displayPreferences.colorBy}
        onColorByToggle={() => handleColorByToggle('subspecialization')}
      />

      {/* טווחי חשיפה למניות */}
      <ExposureRangesFilterList
        selectedRanges={filters.stockExposureRanges}
        onToggle={handleStockExposureRangeToggle}
        title="טווחי חשיפה למניות (%)"
        pinnedRanges={filters.displayPreferences.pinnedItems.stockExposureRanges}
        onTogglePin={(value) => handleTogglePin('stockExposureRanges', value)}
        colorByMode={filters.displayPreferences.colorBy}
        onColorByToggle={() => handleColorByToggle('stock_exposure')}
        colorByValue="stock_exposure"
        ranges={stockExposureRanges}
        rangeColors={stockExposureColors}
      />

      {/* טווחי חשיפה לחו"ל */}
      <ExposureRangesFilterList
        selectedRanges={filters.foreignExposureRanges}
        onToggle={handleForeignExposureRangeToggle}
        title='טווחי חשיפה לחו"ל (%)'
        pinnedRanges={filters.displayPreferences.pinnedItems.foreignExposureRanges}
        onTogglePin={(value) => handleTogglePin('foreignExposureRanges', value)}
        colorByMode={filters.displayPreferences.colorBy}
        onColorByToggle={() => handleColorByToggle('foreign_exposure')}
        colorByValue="foreign_exposure"
        ranges={foreignExposureRanges}
        rangeColors={foreignExposureColors}
      />

      {/* טווחי חשיפה למט"ח */}
      <ExposureRangesFilterList
        selectedRanges={filters.currencyExposureRanges}
        onToggle={handleCurrencyExposureRangeToggle}
        title='טווחי חשיפה למט"ח (%)'
        pinnedRanges={filters.displayPreferences.pinnedItems.currencyExposureRanges}
        onTogglePin={(value) => handleTogglePin('currencyExposureRanges', value)}
        colorByMode={filters.displayPreferences.colorBy}
        onColorByToggle={() => handleColorByToggle('currency_exposure')}
        colorByValue="currency_exposure"
        ranges={currencyExposureRanges}
        rangeColors={currencyExposureColors}
      />
      </Collapse>
    </Box>
   

      {/* שדות לתצוגה */}
      <Box>
        <Typography
          variant="subtitle1"
          onClick={() => setSectionOpen(prev => ({ ...prev, yAxis: !prev.yAxis }))}
          sx={{ mb: 1, fontWeight: 600, textAlign: "right", bgcolor: 'grey.800', color: 'white', px: 1.5, py: 0.5, borderRadius: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          ערך בציר ה Y
          <ExpandMoreIcon sx={{ color: 'white', transform: sectionOpen.yAxis ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
        </Typography>
        <Collapse in={sectionOpen.yAxis}>
        {Object.entries(groupedFields).map(([section, fields]) => {
          const isExpanded = expandedSections[section] ?? false;
          return (
            <Box key={section} sx={{ mb: 1 }}>
              <Box
                sx={{
                  mb: 1,
                  bgcolor: theme.palette.grey[200],
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
                onClick={() => handleToggleSection(section)}
              >
                <IconButton size="small" sx={{ ml: 1 }}>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    textAlign: "right",
                    color: theme.palette.text.primary,
                    px: 2,
                    py: 1,
                    flex: 1,
                  }}
                >
                  {section}
                </Typography>
              </Box>
              <Collapse in={isExpanded}>
                <List dense sx={{ bgcolor: theme.palette.background.paper, borderRadius: 1 }}>
                  {fields.map(({ field, label }) => {
                    const hasExplanation = !!getFieldExplanation(field);
                    return (
                      <ListItem key={field} disablePadding>
                        <ListItemButton onClick={() => handleYAxisFieldChange(field)} dense>
                          <Checkbox
                            edge="end"
                            checked={filters.yAxisField === field}
                            tabIndex={-1}
                            disableRipple
                          />
                          <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                            <ListItemText
                              primary={label}
                              sx={{ textAlign: "right" }}
                            />
                            {hasExplanation && (
                              <Tooltip title="לחץ למידע נוסף">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenExplanation(field, label);
                                  }}
                                  sx={{ p: 0.25 }}
                                >
                                  <InfoOutlinedIcon sx={{ fontSize: 16, color: theme.palette.info.main }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
        </Collapse>
      </Box>
      {/* דיאלוג הסבר שדה */}
      <Dialog
        open={explanationDialogOpen}
        onClose={handleCloseExplanation}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { direction: "rtl" }
        }}
      >
        <DialogTitle sx={{ textAlign: "right", display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlinedIcon color="info" />
          {selectedFieldExplanation?.label}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-line',
              textAlign: 'right',
              lineHeight: 1.8
            }}
          >
            {selectedFieldExplanation?.explanation}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-start", px: 3, pb: 2 }}>
          <Button onClick={handleCloseExplanation} variant="contained">
            סגור
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GemelInvestigationPanel;
